<script setup>
import Avatar from "../../../../common/Avatar.vue";
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import {computed} from "vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const navigationColor = computed(() => portalContentAndDesignStore.portalLayoutSettings?.portal_layout_navigation_color ?? 'white');

const props = defineProps({
    isMobile: {
        type: Boolean,
        default: false,
    },
    isSidebarMenu: {
        type: Boolean,
        default: false,
    }
})

const profileDropdownButtonClasses = computed(() => {

    if (props.isMobile) {
        const classes = ['flex justify-center items-center w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-opacity-10']

        navigationColor.value === 'white'
            ? classes.push('focus:ring-black')
            : classes.push('focus:ring-white')

        return classes.join(' ')
    } else {
        return 'flex rounded-full focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-white'
    }
});

const sidebarAvatarDetails = computed(() => {
    return {
        image_url: portalContentAndDesignStore.avatarDetails?.image_url || null,
        initials: portalContentAndDesignStore.avatarDetails?.initials || 'NN',
        html_class: 'w-10 h-10 text-sm',
        full_name: portalContentAndDesignStore.avatarDetails?.full_name || ''
    }
})

const getMenuItemTitle = (menuItem) => {
    // If menu item has a store_key, use the translation from the store
    if (menuItem.store_key && portalContentAndDesignStore.translationSettings) {
        const translation = portalContentAndDesignStore.translationSettings[menuItem.store_key];
        if (translation && translation.trim() !== '') {
            return translation;
        }
    }
    // Fallback to the title from backend
    return menuItem.title || '';
}

</script>

<template>
    <div v-if="!isSidebarMenu" class="relative flex-shrink-0">
        <options>
            <template #toggle>

                <button type="button"
                        :class="profileDropdownButtonClasses"
                        id="user-menu" aria-expanded="false"
                        aria-haspopup="true"
                >
                    <span class="sr-only">Open user menu</span>
                    <avatar v-if="portalContentAndDesignStore.avatarDetails"
                            :avatar-details="portalContentAndDesignStore.avatarDetails"
                    />
                    <svg v-else
                         xmlns="http://www.w3.org/2000/svg"
                         class="w-8 h-8 text-gray-500"
                         viewBox="0 0 24 24"
                         width="24" height="24" fill="currentColor">
                        <path fill="none" d="M0 0h24v24H0z"/>
                        <path
                            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-3.31 0-10 1.67-10 5v2h20v-2c0-3.33-6.69-5-10-5z"
                        >
                        </path>
                    </svg>
                </button>
            </template>

            <template #items>
                <div class="flex flex-col py-2 px-5">
                    <span class="text-sm tracking-wide">
                        {{ portalContentAndDesignStore.authUser?.name || 'No name' }} {{ portalContentAndDesignStore.authUser?.last_name || '' }}
                    </span>
                    <span class="text-xs tracking-wide text-black text-opacity-50">
                        {{ portalContentAndDesignStore.authUser?.email || 'No email' }}
                    </span>
                </div>
                <div class="px-2 py-0.5">
                    <RouterLink
                        v-for="menuItem in portalContentAndDesignStore.userMenu"
                        class="block px-3 py-1.5 text-sm tracking-wide leading-5 hover:bg-gray-200 hover:bg-opacity-50 focus:outline-none w-full text-left min-w-48 rounded dark:text-gray-400 dark:hover:bg-gray-800"
                        :to="menuItem['path']"
                        :key="menuItem['name']"
                    >
                        {{ getMenuItemTitle(menuItem) }}
                    </RouterLink>
                </div>

                <options-divider></options-divider>

                <options-item
                    :href="portalContentAndDesignStore.getPartnerLogoutRoute"
                    :title="portalContentAndDesignStore.translationSettings.portal_translation_navigation_logout"
                ></options-item>
            </template>
        </options>
    </div>
    <div v-else class="relative py-6 px-8 w-64">
        <sidebar-menu position="left-56">
            <template #toggle>
                <div class="flex items-center -mr-2 cursor-pointer">
                    <div class="flex-shrink-0">
                        <avatar v-if="portalContentAndDesignStore.avatarDetails"
                                :avatar-details="sidebarAvatarDetails"
                        />
                        <svg v-else
                             xmlns="http://www.w3.org/2000/svg"
                             class="w-8 h-8 text-gray-500"
                             viewBox="0 0 24 24"
                             width="24" height="24" fill="currentColor">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path
                                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-3.31 0-10 1.67-10 5v2h20v-2c0-3.33-6.69-5-10-5z"
                            >
                            </path>
                        </svg>
                    </div>
                    <div
                        :class="[
                            'flex-1 ml-3 min-w-0',
                             navigationColor === 'brand' || navigationColor === 'black' ? 'text-white' : ''
                        ]"
                    >
                        <div class="text-sm font-medium truncate">{{ portalContentAndDesignStore.authUser?.name || 'No name' }}</div>
                        <div class="text-xs opacity-60 truncate">{{ portalContentAndDesignStore.authUser?.email || 'No email' }}</div>
                    </div>
                </div>
            </template>

            <template #items>
                <div class="px-2 py-0.5">
                    <RouterLink
                        v-for="menuItem in portalContentAndDesignStore.userMenu"
                        class="block px-3 py-1.5 text-sm tracking-wide leading-5 hover:bg-gray-200 hover:bg-opacity-50 focus:outline-none w-full text-left min-w-48 rounded dark:text-gray-400 dark:hover:bg-gray-800"
                        :to="menuItem['path']"
                        :key="menuItem['name']"
                    >
                        {{ getMenuItemTitle(menuItem) }}
                    </RouterLink>
                </div>

                <options-divider></options-divider>

                <options-item
                    :href="portalContentAndDesignStore.getPartnerLogoutRoute"
                    :title="portalContentAndDesignStore.translationSettings.portal_translation_navigation_logout"
                ></options-item>
            </template>
        </sidebar-menu>
    </div>
</template>

<style scoped>

</style>
